import type { AdvisorsPaths } from '@src/components/sco/Advisors/paths'
import { bookSessionLabel } from '@src/components/sco/Advisors/utils/sessionFormat'

export type AdvisorMarketplaceActionKey = 'invite' | 'book' | 'message' | 'profile'

export type AdvisorMarketplaceAction = {
  key: AdvisorMarketplaceActionKey
  label: string
  variant: 'cta' | 'outlineSolid'
}

export type AdvisorMarketplaceActionContext = {
  canAcceptSessions?: boolean | null
  /** Founder has draft or searching seats (recruit surface). */
  hasOpenSeats: boolean
  /** Own card: no invite / book / message — keep View Profile for directory. */
  isSelf?: boolean
}

/**
 * Directory / profile CTAs. Invite is the recruit primary when the founder has
 * open seats. Book is hidden unless the advisor can accept sessions (including $0).
 */
export const advisorMarketplaceActions = (
  ctx: AdvisorMarketplaceActionContext
): AdvisorMarketplaceAction[] => {
  if (ctx.isSelf) {
    return [
      {
        key: 'profile',
        label: 'View Profile',
        variant: 'outlineSolid',
      },
    ]
  }

  const invite: AdvisorMarketplaceAction = {
    key: 'invite',
    label: 'Invite to Board',
    variant: 'outlineSolid',
  }
  const book = ctx.canAcceptSessions
    ? ({
        key: 'book',
        label: bookSessionLabel(),
        variant: 'outlineSolid',
      } satisfies AdvisorMarketplaceAction)
    : null

  const message: AdvisorMarketplaceAction | null = ctx.isSelf
    ? null
    : {
        key: 'message',
        label: 'Send Message',
        variant: 'outlineSolid',
      }

  const profile: AdvisorMarketplaceAction = {
    key: 'profile',
    label: 'View Profile',
    variant: 'outlineSolid',
  }

  const withTrailing = (actions: AdvisorMarketplaceAction[]) => [
    ...actions,
    ...(message ? [message] : []),
    profile,
  ]

  if (ctx.hasOpenSeats) {
    invite.variant = 'cta'
    return withTrailing(book ? [invite, book] : [invite])
  }
  if (book) {
    book.variant = 'cta'
    return withTrailing([book, invite])
  } else {
    invite.variant = 'cta'
  }
  return withTrailing([invite])
}

/** Book, Send message, and View Profile are links. Invite stays a modal. */
export const advisorMarketplaceActionHref = (
  action: AdvisorMarketplaceAction,
  userId: string,
  paths: Pick<AdvisorsPaths, 'sessionNew' | 'inbox' | 'profile' | 'withQuery'>,
  slug?: string | null,
  /** L18 attribution carried to the book page as `?ref=` (`directory`, `profile`, …). */
  bookingRef?: string | null
): string | null => {
  if (action.key === 'book') {
    return paths.withQuery(
      paths.sessionNew,
      bookingRef ? { expertUserId: userId, ref: bookingRef } : { expertUserId: userId }
    )
  }
  if (action.key === 'message') {
    return paths.withQuery(paths.inbox, { advisorUserId: userId })
  }
  if (action.key === 'profile') {
    return `${paths.profile}/${slug || userId}`
  }
  return null
}
